import type { PresentationStyle } from 'paintscout'
import Color from 'color'
import { defaultPresentationStyle } from './presentationDefaults'

/**
 * The presentation's theme colour.
 *
 * `palette.colors.accent` is the v2 field. `accentColor` is the deprecated v1 flat colour, kept
 * as a fallback for presentations converted from v1 (see PresentationStyle in
 * packages/@types/@paintscout/presentation.d.ts).
 *
 * Single source of truth for the accent — both getPresentationTheme (content CSS) and the nav /
 * quick cart CTA buttons read it through here, so the two cannot drift.
 */
export function getPresentationAccentColor(presentationStyle?: PresentationStyle): string {
  const style = presentationStyle || defaultPresentationStyle

  return style.palette?.colors?.accent || style.accentColor || defaultPresentationStyle.accentColor
}

/**
 * Colours for a presentation CTA button that lives *outside* the themed content subtree — the
 * accept and pay buttons in PresentationNav and the quick cart drawer.
 *
 * Those buttons never see the presentation theme: getPresentationTheme's output is only turned
 * into CSS for the content area, and the nav is a sibling of it. So they need the colours handed
 * to them directly.
 *
 * Values mirror the `.themeButton` rule in
 * packages/@paintscout/ui/src/styles/getPresentationContentStylesNext.ts, so an in-page theme
 * button and a nav CTA match.
 */
export function getPresentationButtonColors(presentationStyle?: PresentationStyle): {
  backgroundColor: string
  color: string
} {
  const backgroundColor = getPresentationAccentColor(presentationStyle)

  let color = '#FFF'
  try {
    color = Color(backgroundColor).isLight() ? '#000' : '#FFF'
  } catch (err) {
    // an accent colour `color` can't parse would otherwise throw the whole nav away — the
    // background still renders as whatever the browser makes of the string. Warn so a
    // wrong-coloured or invisible CTA is diagnosable instead of silent.
    console.warn(
      `getPresentationButtonColors: could not parse accent colour "${backgroundColor}", ` +
        `defaulting the button text to ${color}`,
      err
    )
  }

  return { backgroundColor, color }
}
